/**
 * Turning untrusted JSON into a typed {@link Command}.
 *
 * Commands arrive from three places TypeScript cannot vouch for: IndexedDB
 * (written by an older build), the sync WebSocket (written by another device),
 * and the AI assistant's `propose_changes` tool (written by a language model).
 * All three go through here.
 *
 * The parse is written by hand rather than as a 48-branch
 * `z.discriminatedUnion`, for the error messages: an unrecognised type should
 * say *"unknown command type"*, not "expected one of 48 literals", and an
 * invalid payload should report the field that is wrong rather than 48 failed
 * alternatives.
 */

import { z } from 'zod';
import type { CommandError } from './envelope';
import { actorSchema, commandError, issuesFromZod } from './envelope';
import { COMMAND_DEFINITIONS, isCommandType } from './registry';
import type { Command } from './types';

/** The envelope shape, before the payload is known. */
const rawEnvelopeSchema = z.strictObject({
  commandId: z.string().min(1).max(64),
  type: z.string().min(1).max(64),
  timestamp: z.number().int().min(0),
  actor: actorSchema,
  payload: z.unknown(),
});

/** The result of {@link parseCommand}. */
export type CommandParseResult =
  | { readonly ok: true; readonly command: Command }
  | { readonly ok: false; readonly error: CommandError };

/**
 * Validate an unknown value as a command.
 *
 * @param value - Anything: a `JSON.parse` result, a tool-call argument object.
 * @returns The typed command, or a {@link CommandError} explaining why not.
 *   Never throws.
 */
export function parseCommand(value: unknown): CommandParseResult {
  const envelope = rawEnvelopeSchema.safeParse(value);
  if (!envelope.success) {
    return {
      ok: false,
      error: commandError('malformed-envelope', 'Not a command envelope.', {
        issues: issuesFromZod(envelope.error),
      }),
    };
  }
  const { commandId, type, timestamp, actor, payload } = envelope.data;
  if (!isCommandType(type)) {
    return {
      ok: false,
      error: commandError('unknown-command', `Unknown command type "${type}".`, {
        commandId,
        commandType: type,
      }),
    };
  }
  const definition: { payload: z.ZodType<unknown> } = COMMAND_DEFINITIONS[type];
  const parsed = definition.payload.safeParse(payload);
  if (!parsed.success) {
    return {
      ok: false,
      error: commandError('invalid-payload', `Invalid payload for "${type}".`, {
        commandId,
        commandType: type,
        issues: issuesFromZod(parsed.error),
      }),
    };
  }
  return {
    ok: true,
    command: { commandId, type, timestamp, actor, payload: parsed.data } as Command,
  };
}

/** Whether an unknown value is a valid command. */
export function isCommand(value: unknown): value is Command {
  return parseCommand(value).ok;
}

/**
 * A Zod schema for a command, for embedding in larger schemas (the changeset,
 * an AI tool definition, a sync frame).
 *
 * Detail-light by design — use {@link parseCommand} when you want to tell the
 * user *which* field was wrong.
 */
export const commandSchema = z.custom<Command>((value) => parseCommand(value).ok, {
  message: 'not a valid Beerwright command envelope',
});
